/**
 * User Profile Modal
 *
 * Modal component for viewing detailed user profile information.
 * Displays comprehensive user data in a read-only format with option to edit.
 *
 * Features:
 * - Complete user profile view
 * - Avatar display with initials fallback
 * - Status indicator (Active/Inactive)
 * - Last login information
 * - System settings display
 * - Quick edit action button
 * - Organized information sections
 *
 * @component
 */

import React from 'react';
import {
  X,
  Edit,
  Mail,
  Phone,
  Briefcase,
  Building,
  Calendar,
  Clock,
  User as UserIcon,
  Shield,
  Globe,
  MessageSquare,
  CheckCircle,
  XCircle
} from 'lucide-react';
import {
  getUserFullName,
  getUserInitials,
  formatLastLogin,
  type User
} from '../../../services/queries/userQueries';
import { StandardButton } from '../../standard';

// ============================================
// INTERFACES
// ============================================

interface UserProfileModalProps {
  isOpen: boolean;
  user: User | null;
  onClose: () => void;
  onEdit: () => void;
}

interface InfoRowProps {
  icon: React.ElementType;
  label: string;
  value: string | React.ReactNode;
}

// ============================================
// SUB-COMPONENTS
// ============================================

const InfoRow: React.FC<InfoRowProps> = ({ icon: Icon, label, value }) => {
  return (
    <div className="flex items-start py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <div className="flex items-center min-w-[140px]">
        <Icon className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-2" />
        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{label}</span>
      </div>
      <div className="flex-1 text-sm text-gray-900 dark:text-gray-100">
        {value || <span className="text-gray-400 dark:text-gray-500">Not specified</span>}
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

export const UserProfileModal: React.FC<UserProfileModalProps> = ({
  isOpen,
  user,
  onClose,
  onEdit
}) => {
  if (!isOpen || !user) return null;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="relative">
          {/* Cover Background */}
          <div className="h-32 bg-gradient-to-r from-purple-600 to-blue-600 rounded-t-lg" />

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:text-gray-200 bg-black bg-opacity-30 rounded-full p-2"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Avatar and Basic Info */}
          <div className="px-6 pb-6">
            <div className="flex items-end -mt-16 mb-4">
              {user.profilePicture ? (
                <img
                  src={user.profilePicture}
                  alt={getUserFullName(user)}
                  className="h-32 w-32 rounded-full border-4 border-white dark:border-gray-800 shadow-lg"
                />
              ) : (
                <div className="h-32 w-32 rounded-full border-4 border-white dark:border-gray-800 bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center shadow-lg">
                  <span className="text-purple-600 dark:text-purple-400 font-bold text-4xl">
                    {getUserInitials(user)}
                  </span>
                </div>
              )}

              <div className="ml-6 flex-1">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                      {getUserFullName(user)}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 text-lg">
                      @{user.username}
                    </p>
                  </div>
                  <StandardButton
                    onClick={onEdit}
                    variant="primary"
                    leftIcon={<Edit className="h-4 w-4" />}
                  >
                    Edit Profile
                  </StandardButton>
                </div>

                {/* Status Badge */}
                <div className="mt-3">
                  {user.isActive ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Active User
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                      <XCircle className="h-4 w-4 mr-1" />
                      Inactive User
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Contact Information */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <UserIcon className="h-5 w-5 mr-2 text-purple-600 dark:text-purple-400" />
                Contact Information
              </h3>
              <div className="space-y-1">
                <InfoRow icon={Mail} label="Email" value={user.email} />
                <InfoRow icon={Phone} label="Phone" value={user.phone} />
                <InfoRow icon={Briefcase} label="Title" value={user.title} />
                <InfoRow icon={Building} label="Department" value={user.department} />
              </div>
            </div>

            {/* Role & Permissions */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <Shield className="h-5 w-5 mr-2 text-purple-600 dark:text-purple-400" />
                Role & Permissions
              </h3>
              <div className="space-y-1">
                <InfoRow
                  icon={Shield}
                  label={user.roleNames && user.roleNames.length > 1 ? "Roles" : "Role"}
                  value={
                    user.roleNames && user.roleNames.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {user.roleNames.map((roleName, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                          >
                            {roleName}
                          </span>
                        ))}
                      </div>
                    ) : user.roleName ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                        {user.roleName}
                      </span>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">No role assigned</span>
                    )
                  }
                />
                <InfoRow icon={Calendar} label="Created" value={formatDate(user.createdDate)} />
                <InfoRow icon={UserIcon} label="Created By" value={user.createdBy} />
                <InfoRow icon={Clock} label="Last Modified" value={formatDate(user.lastModified)} />
              </div>
            </div>

            {/* System Settings */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <Globe className="h-5 w-5 mr-2 text-purple-600 dark:text-purple-400" />
                System Settings
              </h3>
              <div className="space-y-1">
                <InfoRow icon={Globe} label="Locale" value={user.locale || 'en_US'} />
                <InfoRow icon={Clock} label="Timezone" value={user.timezone || 'Not set'} />
                <InfoRow
                  icon={Clock}
                  label="Last Login"
                  value={
                    <span className={user.lastLogin ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}>
                      {formatLastLogin(user.lastLogin)}
                    </span>
                  }
                />
              </div>
            </div>

            {/* Additional Information */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <MessageSquare className="h-5 w-5 mr-2 text-purple-600 dark:text-purple-400" />
                Additional Information
              </h3>
              <div className="space-y-1">
                <InfoRow icon={UserIcon} label="User ID" value={user.id} />
                {user.emailSignature && (
                  <div className="pt-3">
                    <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                      Email Signature
                    </div>
                    <div className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap bg-white dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700">
                      {user.emailSignature}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Activity Summary */}
          <div className="mt-6 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                  Account Status
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  User account created on {formatDate(user.createdDate)}
                  {user.lastLogin && (
                    <> • Last active {formatLastLogin(user.lastLogin)}</>
                  )}
                </p>
              </div>
              {user.isActive && (
                <div className="flex items-center text-green-600 dark:text-green-400">
                  <div className="h-2 w-2 bg-green-500 rounded-full mr-2 animate-pulse" />
                  <span className="text-sm font-medium">Online Status Available</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 rounded-b-lg">
          <div className="flex justify-between items-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Profile information is managed by System Administrators
            </p>
            <StandardButton
              variant="secondary"
              onClick={onClose}
            >
              Close
            </StandardButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileModal;
